<template>
  <div v-if="options.length > 1" class="animal-filter">
    <span :id="labelId" class="animal-filter__label">Питомец:</span>
    <div class="animal-filter__options" role="group" :aria-labelledby="labelId">
      <template v-for="option in options" :key="option.slug">
        <!-- Текущий вид: идти некуда, но и `disabled` ставить нельзя — он выбрасывает опцию из
             таб-порядка и читается скринридером как «недоступно», из-за чего фильтр выглядел
             сломанным целиком. -->
        <span v-if="option.selected" class="animal-filter__option _active" aria-current="page">
          {{ option.label }}<span v-if="option.count" class="animal-filter__count">{{ option.count }}</span>
        </span>
        <!-- Вид с собственным лендингом — ссылка, а не состояние страницы: у «Стерилизации кошек»
             свой адрес в выдаче, и фильтр не должен подменять его параметром (ТЗ §5). -->
        <NuxtLink v-else-if="targetFor(option)" :to="targetFor(option)!" class="animal-filter__option">
          {{ option.label }}<span v-if="option.count" class="animal-filter__count">{{ option.count }}</span>
        </NuxtLink>
        <button v-else type="button" class="animal-filter__option"
                aria-pressed="false"
                :disabled="option.disabled"
                @click="emit('select', option.slug)">
          {{ option.label }}<span v-if="option.count" class="animal-filter__count">{{ option.count }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
export type AnimalOption = {
  slug: string;
  label: string;
  count?: number;
  disabled?: boolean;
  selected?: boolean;
  /** Название в родительном падеже: «для кошек предложений пока нет». */
  seoName?: string;
};

const props = defineProps<{
  options: AnimalOption[];
  /** Слаг видового лендинга по слагу вида: только для хабов, у которых лендинги есть. */
  landings?: Record<string, string>;
  /** Страница сама является видовым лендингом: фильтр показывается, но переключать его нечем. */
  fixed?: boolean;
  parentSlug?: string;
  city?: boolean;
}>();

const emit = defineEmits<{ (event: 'select', slug: string): void }>();

const labelId = useId();

/** Опция «Все»: у неё нет корзины вида, поэтому и лендинга быть не может. */
const DEFAULT_SLUG = 'zhivotnyh';

/**
 * Куда ведёт опция. На плоском адресе вид ведёт на собственный лендинг, если он есть.
 *
 * На лендинге переключать нечего: `?animal` там инертен по построению (вид задан адресом), и
 * любой выбор, кроме своего вида, обязан уйти на родительский хаб — своим адресом, если у вида
 * есть лендинг, иначе хаб с параметром. Иначе получается тупик, из которого владелец и не смог
 * выбраться с «Прививки собаке».
 */
const targetFor = (option: AnimalOption): string | null => {
  if (option.disabled) {
    return null;
  }

  if (props.city) {
    return null;
  }

  const landing = props.landings?.[option.slug];

  if (landing) {
    return `/uslugi/${landing}/`;
  }

  if (!props.fixed || !props.parentSlug) {
    return null;
  }

  const hub = `/uslugi/${props.parentSlug}/`;

  return option.slug === DEFAULT_SLUG ? hub : `${hub}?animal=${option.slug}`;
};
</script>

<style lang="scss" scoped>
.animal-filter {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 16px;

  &__label {
    @include light-dark(color, #6b7280, var(--color-gray));
    font-size: 14px;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__option {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid;
    @include light-dark(border-color, #e5e7eb, var(--color-light-gray));
    border-radius: 999px;
    background: $color-white;
    color: inherit;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
    transition: border-color .15s ease, background-color .15s ease;

    &:hover:not(:disabled) {
      border-color: var(--primary, #2f6fed);
    }

    &._active {
      border-color: var(--primary, #2f6fed);
      @include light-dark(background, #eef4ff, var(--color-info));
      cursor: default;
    }

    &:disabled {
      opacity: .5;
      cursor: default;
    }
  }

  &__count {
    @include light-dark(color, #9ca3af, var(--color-gray));
    font-size: 12px;
  }
}
</style>
